import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { useGetInvestmentFundsQuery, useGetReportingsQuery } from 'api/main';
import { billStatus as reportingStatus } from 'utils/constants';
import { handleDocumentPreview } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { CustomSelect, ListContainer, Loader } from 'components';
import { Box, MenuItem, Paper, Typography } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    paper: {
        minHeight: 450,
        padding: theme.spacing(2),
        marginTop: theme.spacing(3),
        borderRadius: 8,
        border: `1px solid ${theme.palette.grey[100]}`,
    },
    docName: {
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
    },
}));

const DistributorReporting = () => {
    const classes = useStyles();

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery();
    const { data: reportings = [], isLoading: reportingsLoader } = useGetReportingsQuery({
        pagination: false,
    });

    const [selectedFund, setSelectedFund] = useState('');
    const [downloadingDoc, setDownloadingDoc] = useState(null);

    const selectedFundName = useMemo(
        () => fundList.find(f => f.id === +selectedFund)?.name,
        [fundList, selectedFund]
    );

    const eligibleReportings = useMemo(
        () =>
            reportings.filter(
                r =>
                    (r.status === reportingStatus.ACTIVATED ||
                        r.status === reportingStatus.COMPLETED) &&
                    r.recipientType === 'distributors'
            ),
        [reportings]
    );

    const filteredReportings = useMemo(
        () => eligibleReportings.filter(r => r.investmentFund.id === +selectedFund),
        [eligibleReportings, selectedFund]
    );

    const filteredFunds = useMemo(
        () => fundList.filter(f => !!eligibleReportings.find(r => r.investmentFund.id === f.id)),
        [fundList, eligibleReportings]
    );

    const handlePreview = useCallback(doc => {
        handleDocumentPreview(doc, setDownloadingDoc);
    }, []);

    useEffect(() => {
        if (filteredFunds.length) {
            setSelectedFund(filteredFunds[0].id);
        }
    }, [filteredFunds]);

    return (
        <ListContainer>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.REPORTING}
            </Typography>
            <Box pl={2}>
                <Loader loading={fundsLoader || reportingsLoader} marginTop={15}>
                    {filteredReportings.length ? (
                        <>
                            <Box width={260} mt={3}>
                                <CustomSelect
                                    label={texts.FUND}
                                    value={selectedFund}
                                    onChange={e => setSelectedFund(+e.target.value)}
                                >
                                    {filteredFunds.map(f => (
                                        <MenuItem key={f.id} value={f.id}>
                                            {f.name}
                                        </MenuItem>
                                    ))}
                                </CustomSelect>
                            </Box>
                            <Paper className={classes.paper}>
                                <Typography variant="h5" color="primary">
                                    {selectedFundName}
                                </Typography>
                                <Box mt={2} pl={2} display="flex" flexDirection="column">
                                    {filteredReportings.map(r => (
                                        <Typography
                                            key={r.id}
                                            className={classes.docName}
                                            onClick={() => handlePreview(r.document)}
                                            component="span"
                                        >
                                            <Loader
                                                loading={downloadingDoc === r.document.id}
                                                size={20}
                                                marginTop={0}
                                            >
                                                {`${texts.REPORT_AT} ${moment(r.date).format(
                                                    'L'
                                                )} - ${r.investmentFund.name}`}
                                            </Loader>
                                        </Typography>
                                    ))}
                                </Box>
                            </Paper>
                        </>
                    ) : (
                        <Typography variant="h6" align="left">
                            {texts.AUCUN_REPORT}
                        </Typography>
                    )}
                </Loader>
            </Box>
        </ListContainer>
    );
};

export default DistributorReporting;
